/**
 * Deviation Service – PRD §3 (Deviation Object & Michelangelo Engine)
 * Unified PRD-compliant deviation schema. System-generated only.
 * Links to: task, protocol, risk, standard requirements, entity, threshold.
 */

import { collection, query, where, getDocs, getDoc, doc, orderBy, Timestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { deviationThresholdService } from './deviationThresholdService';
import protocolProcessMappingService from './protocolProcessMappingService';
import qmsStandardService from './qmsStandardService';

/**
 * Serialize Firestore Timestamps for callable (server revives with Admin SDK).
 */
function serializeTimestampsForCallable(obj) {
  if (obj == null) return obj;
  if (typeof obj === 'object' && typeof obj.toMillis === 'function') {
    return { __ts: obj.toMillis() };
  }
  if (Array.isArray(obj)) {
    return obj.map(serializeTimestampsForCallable);
  }
  if (typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = serializeTimestampsForCallable(v);
  }
  return out;
}

async function persistDeviationViaCallable(deviation) {
  const functions = getFunctions(getApp(), 'us-central1');
  const createDeviation = httpsCallable(functions, 'createDeviation');
  const payload = serializeTimestampsForCallable(deviation);
  const result = await createDeviation({ deviation: payload });
  const deviationId = result.data?.deviationId;
  if (!deviationId) {
    throw new Error('[DeviationService] createDeviation callable returned no deviationId');
  }
  return deviationId;
}

export class DeviationService {
  constructor() {
    this.deviationsCollection = collection(db, 'deviations');
  }

  /**
   * Resolve deviation threshold scope for a protocol (standardId, requirementId, standardRequirementId).
   * Used to fetch per-standard/per-requirement thresholds when detecting deviations.
   */
  async resolveDeviationScopeForProtocol(protocolId) {
    if (!protocolId) return {};
    try {
      const mappings = await protocolProcessMappingService.getProcessesForProtocol(protocolId);
      const processIds = [...new Set(mappings.map((m) => m.processId).filter(Boolean))];
      if (processIds.length === 0) return { standardId: null, requirementId: null, standardRequirementId: null };

      const srs = await qmsStandardService.getStandardRequirementsForStandards(processIds);
      const firstSr = srs[0];
      return {
        standardId: processIds[0] || null,
        requirementId: firstSr?.requirementId || null,
        standardRequirementId: firstSr?.id || null
      };
    } catch (_) {
      return {};
    }
  }

  /**
   * Resolve standard requirement IDs and codes for a protocol (PRD §3.4 standardRequirementIds).
   * Protocol → process (via protocol_process_mapping) → standard_requirements → requirementIds.
   */
  async resolveStandardRequirementIdsForProtocol(protocolId) {
    if (!protocolId) return { requirementIds: [], requirementCodes: [], standardCodes: [] };
    try {
      const mappings = await protocolProcessMappingService.getProcessesForProtocol(protocolId);
      const processIds = [...new Set(mappings.map((m) => m.processId).filter(Boolean))];
      if (processIds.length === 0) return { requirementIds: [], requirementCodes: [], standardCodes: mappings.map((m) => m.processCode || m.processName).filter(Boolean) };

      const srs = await qmsStandardService.getStandardRequirementsForStandards(processIds);
      const requirementIds = [...new Set(srs.map((sr) => sr.requirementId).filter(Boolean))];
      const standardCodes = mappings.map((m) => m.processCode || m.processName).filter(Boolean);

      let requirementCodes = [];
      if (requirementIds.length > 0) {
        try {
          const qmsPolicyService = (await import('./qmsPolicyService')).default;
          for (const rid of requirementIds.slice(0, 10)) {
            const req = await qmsPolicyService.getRequirement(rid).catch(() => null);
            if (req?.code) requirementCodes.push(req.code);
          }
        } catch (_) {}
      }
      return { requirementIds, requirementCodes, standardCodes };
    } catch (err) {
      console.warn('Resolve standard requirements for protocol failed:', err);
      return { requirementIds: [], requirementCodes: [], standardCodes: [] };
    }
  }

  /**
   * Build PRD §3.3 deviation statement.
   * "[Biological entity] risk is elevated in [location] because [procedure] execution on [datetime] by [assignee] failed at: [specific steps/actions]. Standard [code] is at risk."
   */
  buildDeviationStatement({
    biologicalEntities = [],
    locationPath = '—',
    protocolName = 'procedure',
    executedAt,
    assigneeName = '',
    failedSteps = [],
    standardCodes = []
  }) {
    const bio = biologicalEntities?.length ? biologicalEntities.join(', ') : 'Biological entity';
    const datetime = executedAt ? (executedAt.toDate ? executedAt.toDate().toLocaleString() : new Date(executedAt).toLocaleString()) : '—';
    const assignee = assigneeName || '—';
    const stepsText = failedSteps?.length
      ? failedSteps.map((s) => `${s.stepName || s.stepId || 'Step'}: ${(s.failedActions || []).map((a) => a.actionName || a.failureType).join(', ') || 'incomplete'}`).join('; ')
      : '—';
    const stdCodes = standardCodes?.length ? standardCodes.join(', ') : '—';
    return `${bio} risk is elevated in ${locationPath} because ${protocolName} execution on ${datetime} by ${assignee} failed at: ${stepsText}. Standard ${stdCodes} at risk.`;
  }

  /**
   * Check for recurring deviation on same entity (PRD §3.4 occurrenceCount, isRecurring).
   */
  async checkRecurringDeviation(organizationId, entityType, entityId, protocolId) {
    if (!organizationId || !entityId || !protocolId) return { occurrenceCount: 1, firstOccurredAt: null, isRecurring: false };
    try {
      const q = query(
        this.deviationsCollection,
        where('organizationId', '==', organizationId),
        where('entityType', '==', entityType),
        where('entityId', '==', entityId),
        where('protocolId', '==', protocolId)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (docs.length === 0) return { occurrenceCount: 1, firstOccurredAt: null, isRecurring: false };
      const first = docs.reduce((a, b) => {
        const atA = a.createdAt?.toMillis?.() ?? a.executedAt?.toMillis?.() ?? 0;
        const atB = b.createdAt?.toMillis?.() ?? b.executedAt?.toMillis?.() ?? 0;
        return atA < atB ? a : b;
      });
      return {
        occurrenceCount: docs.length + 1,
        firstOccurredAt: first.createdAt || first.executedAt,
        isRecurring: docs.length >= 1
      };
    } catch (_) {
      return { occurrenceCount: 1, firstOccurredAt: null, isRecurring: false };
    }
  }

  /**
   * Create PRD-compliant deviation (unified schema for both step-level and threshold-breach).
   */
  async createDeviationWithContext(deviationData, context) {
    const now = Timestamp.now();
    const {
      organizationId,
      taskInstanceId,
      taskExecutionId,
      protocolId,
      protocolName,
      departmentId,
      roomId,
      entityType,
      entityId,
      locationPath,
      executorId,
      executorName,
      riskId,
      riskName,
      biologicalEntities,
      rpnAtTime,
      thresholdId,
      thresholdType,
      thresholdValue,
      incidentIds,
      failedSteps,
      expectedComplianceValue,
      actualComplianceValue,
      gapValue
    } = context;

    const { requirementIds, requirementCodes, standardCodes } = await this.resolveStandardRequirementIdsForProtocol(protocolId);
    const locPath = locationPath || [departmentId, roomId].filter(Boolean).join(' / ') || '—';
    const entity = entityType || (roomId ? 'room' : 'department');
    const eid = entityId || roomId || departmentId || '';

    const recurring = await this.checkRecurringDeviation(organizationId, entity, eid, protocolId);

    const deviationCode = await this.generateDeviationCode();
    const deviationStatement = this.buildDeviationStatement({
      biologicalEntities: biologicalEntities || [],
      locationPath: locPath,
      protocolName: protocolName || 'procedure',
      executedAt: now,
      assigneeName: executorName || '',
      failedSteps: failedSteps || [],
      standardCodes: standardCodes.length ? standardCodes : requirementCodes
    });

    const deviation = {
      deviation_code: deviationCode,
      organizationId: organizationId || null,
      createdAt: now,

      taskInstanceId: taskInstanceId || taskExecutionId || null,
      task_execution_id: taskExecutionId || null,
      protocolId: protocolId || '',
      protocolName: protocolName || '',
      expectedComplianceValue: expectedComplianceValue ?? 100,
      actualComplianceValue: actualComplianceValue ?? 0,
      gapValue: gapValue ?? (expectedComplianceValue ?? 100) - (actualComplianceValue ?? 0),

      failedSteps: failedSteps || [],
      assignedTo: executorId || '',
      assignedToName: executorName || '',
      executedAt: now,

      entityType: entity,
      entityId: eid,
      entityName: locPath,
      locationPath: locPath,

      riskId: riskId || '',
      riskName: riskName || '',
      biologicalEntities: biologicalEntities || [],
      rpnAtTime: rpnAtTime ?? 0,

      thresholdId: thresholdId || '',
      thresholdType: thresholdType || 'absolute',
      thresholdValue: thresholdValue ?? 0,

      standardRequirementIds: requirementIds,
      policyDocumentIds: [],

      incidentIds: incidentIds || [],

      occurrenceCount: recurring.occurrenceCount,
      firstOccurredAt: recurring.firstOccurredAt,
      isRecurring: recurring.isRecurring,

      deviation_type: deviationData.deviation_type || 'execution',
      deviation_category: deviationData.deviation_category || deviationData.deviation_type,
      severity: deviationData.severity || 'minor',
      description: deviationData.description || '',
      detected_by: deviationData.detected_by || 'system',
      detection_method: deviationData.detection_method || 'automated',

      actual_rpn_impact: deviationData.actual_rpn_impact ?? 0,
      compliance_score_impact: deviationData.compliance_score_impact ?? 0,
      patient_safety_impact: deviationData.patient_safety_impact || 'low',

      root_cause: null,
      contributing_factors: [],
      immediate_action_taken: null,
      corrective_action_plan: null,
      preventive_measures: [],

      status: 'open',
      resolved_by: null,
      resolved_at: null,
      resolution_notes: null,
      rcaId: null,
      correctiveActionIds: [],

      requires_retraining: false,
      requires_protocol_update: false,
      requires_escalation: deviationData.severity === 'critical',
      escalated_to: [],

      deviationStatement,
      detected_at: now,
      reported_by: deviationData.reported_by || null,
      reviewed_by: null,
      reviewed_at: null,
      updatedAt: now,
      updatedBy: 'system'
    };

    const id = await persistDeviationViaCallable(deviation);
    console.log(`✅ Created deviation: ${id} (${deviationCode})`);
    return id;
  }

  /**
   * Detect deviations from task execution (uses configurable thresholds).
   * Resolves per-standard/per-requirement thresholds from protocol mapping.
   */
  async detectDeviations(taskExecution, actionData, context = {}) {
    try {
      const organizationId = context.organizationId || taskExecution.organizationId || null;
      const protocolId = context.protocolId || taskExecution.protocol_id || null;
      const scope = await this.resolveDeviationScopeForProtocol(protocolId);

      const deviations = [];

      const timingDeviation = await this.detectTimingDeviation(actionData, organizationId, scope);
      if (timingDeviation) deviations.push(timingDeviation);

      const sequenceDeviation = await this.detectSequenceDeviation(taskExecution, actionData, organizationId, scope);
      if (sequenceDeviation) deviations.push(sequenceDeviation);

      const qualityDeviation = await this.detectQualityDeviation(actionData, organizationId, scope);
      if (qualityDeviation) deviations.push(qualityDeviation);

      const riskDeviation = await this.detectRiskDeviation(taskExecution, actionData, organizationId, scope);
      if (riskDeviation) deviations.push(riskDeviation);

      for (const deviation of deviations) {
        await this.createDeviationWithContext(deviation, {
          ...context,
          taskExecutionId: taskExecution.id,
          taskInstanceId: context.taskInstanceId || taskExecution.task_id || null,
          protocolId: context.protocolId || taskExecution.protocol_id || null,
          protocolName: context.protocolName || '',
          departmentId: context.departmentId || null,
          roomId: context.roomId || null,
          entityType: context.roomId ? 'room' : 'department',
          entityId: context.roomId || context.departmentId || '',
          locationPath: context.locationPath || null,
          executorId: context.executorId || null,
          executorName: context.executorName || '',
          riskId: context.riskId || null,
          riskName: context.riskName || '',
          biologicalEntities: context.biologicalEntities || [],
          rpnAtTime: context.rpnAtTime ?? 0,
          failedSteps: [{
            stepId: actionData.step_id,
            stepName: `Step ${actionData.step_number}`,
            failedActions: [{ actionName: deviation.deviation_type, failureType: 'detected' }]
          }]
        });

        if (deviation.severity === 'critical') {
          await this.triggerCriticalDeviationAlert(deviation);
        }
      }
      return deviations;
    } catch (error) {
      console.error('Error detecting deviations:', error);
      throw error;
    }
  }

  async detectTimingDeviation(actionData, organizationId, scope = {}) {
    const expectedDuration = actionData.expected_duration_seconds || actionData.action?.duration_seconds || 0;
    const actualDuration = actionData.duration_seconds || 0;
    if (expectedDuration === 0 || actualDuration === 0) return null;

    const variance = Math.abs(actualDuration - expectedDuration) / expectedDuration;
    const exceeds = await deviationThresholdService.exceedsTimingThreshold(organizationId, variance, scope);
    if (!exceeds) return null;

    const timing = await deviationThresholdService.getTimingThresholds(organizationId, scope);
    const severity = await deviationThresholdService.getTimingSeverity(organizationId, variance, scope);
    return {
      deviation_type: 'timing',
      deviation_category: 'timing',
      severity,
      description: `Action completed in ${actualDuration}s vs expected ${expectedDuration}s (${Math.round(variance * 100)}% variance)`,
      detected_by: 'system',
      detection_method: 'automated_timing_check',
      step_number: actionData.step_number || null,
      action_number: actionData.action_number || null,
      actual_rpn_impact: 0,
      compliance_score_impact: severity === 'major' ? (timing.complianceImpactMajor ?? 10) : (timing.complianceImpactMinor ?? 5),
      patient_safety_impact: severity === 'major' ? 'medium' : 'low'
    };
  }

  async detectSequenceDeviation(taskExecution, actionData, organizationId, scope = {}) {
    const stepsCompleted = taskExecution.steps_completed || [];
    const currentStepNumber = actionData.step_number || stepsCompleted.length + 1;
    const previousStep = stepsCompleted[stepsCompleted.length - 1];
    if (!previousStep || previousStep.step_number <= currentStepNumber) return null;

    const timing = await deviationThresholdService.getThresholds(organizationId, scope);
    const seq = timing.sequence || {};
    return {
      deviation_type: 'sequence',
      deviation_category: 'sequence',
      severity: seq.severity || 'major',
      description: `Step ${currentStepNumber} completed out of order. Previous step was ${previousStep.step_number}.`,
      detected_by: 'system',
      detection_method: 'automated_sequence_check',
      step_number: currentStepNumber,
      action_number: actionData.action_number || null,
      actual_rpn_impact: 0,
      compliance_score_impact: seq.complianceImpact ?? 15,
      patient_safety_impact: 'medium'
    };
  }

  async detectQualityDeviation(actionData, organizationId, scope = {}) {
    const requiredEvidence = actionData.required_evidence_types || [];
    const missingEvidence = [];
    requiredEvidence.forEach((evidenceType) => {
      if (evidenceType === 'photo' && (!actionData.photo_urls || actionData.photo_urls.length === 0)) missingEvidence.push('photo');
      if (evidenceType === 'signature' && !actionData.signature_url) missingEvidence.push('signature');
    });
    if (missingEvidence.length === 0) return null;

    const timing = await deviationThresholdService.getThresholds(organizationId, scope);
    const qual = timing.quality || {};
    return {
      deviation_type: 'quality',
      deviation_category: 'quality',
      severity: qual.severity || 'minor',
      description: `Missing required evidence: ${missingEvidence.join(', ')}`,
      detected_by: 'system',
      detection_method: 'automated_completeness_check',
      step_number: actionData.step_number || null,
      action_number: actionData.action_number || null,
      actual_rpn_impact: 0,
      compliance_score_impact: qual.complianceImpact ?? 5,
      patient_safety_impact: 'low'
    };
  }

  async detectRiskDeviation(taskExecution, actionData, organizationId, scope = {}) {
    const riskData = taskExecution.risk_data || {};
    const activeRisks = riskData.active_risks || [];
    const rpnThreshold = await deviationThresholdService.getRiskRpnThreshold(organizationId, scope);
    const highRiskActions = activeRisks.filter((risk) => (risk.rpn ?? 0) >= rpnThreshold);
    if (highRiskActions.length === 0 || actionData.validation_passed !== false) return null;

    const timing = await deviationThresholdService.getThresholds(organizationId, scope);
    const risk = timing.risk || {};
    return {
      deviation_type: 'risk',
      deviation_category: 'risk',
      severity: risk.severity || 'critical',
      description: `High-risk action (RPN: ${highRiskActions[0].rpn}) performed incorrectly. Validation failed.`,
      detected_by: 'system',
      detection_method: 'automated_risk_check',
      step_number: actionData.step_number || null,
      action_number: actionData.action_number || null,
      actual_rpn_impact: risk.rpnImpact ?? 50,
      compliance_score_impact: risk.complianceImpact ?? 20,
      patient_safety_impact: 'high'
    };
  }

  /**
   * Legacy createDeviation – now delegates to createDeviationWithContext with minimal context.
   */
  async createDeviation(taskExecutionId, deviationData, context = {}) {
    return this.createDeviationWithContext(deviationData, {
      ...context,
      taskExecutionId,
      taskInstanceId: context.taskInstanceId || null,
      protocolId: context.protocolId || null,
      protocolName: context.protocolName || '',
      departmentId: context.departmentId || null,
      roomId: context.roomId || null,
      executorId: context.executorId || null,
      executorName: context.executorName || '',
      organizationId: context.organizationId || null,
      failedSteps: [{
        stepId: deviationData.step_id,
        stepName: `Step ${deviationData.step_number || '?'}`,
        failedActions: [{ actionName: deviationData.deviation_type || 'unknown', failureType: 'detected' }]
      }]
    });
  }

  async generateDeviationCode() {
    const year = new Date().getFullYear();
    const count = await this.getDeviationCountForYear(year);
    return `DEV-${year}-${String(count + 1).padStart(3, '0')}`;
  }

  async getDeviationCountForYear(year) {
    try {
      const startOfYear = Timestamp.fromDate(new Date(year, 0, 1));
      const endOfYear = Timestamp.fromDate(new Date(year + 1, 0, 1));
      const q = query(
        this.deviationsCollection,
        where('detected_at', '>=', startOfYear),
        where('detected_at', '<', endOfYear)
      );
      const snap = await getDocs(q);
      return snap.size;
    } catch (_) {
      return 0;
    }
  }

  /**
   * Count open deviations for a protocol (PRD requirement row: open deviations impacting this protocol).
   */
  async getOpenDeviationsCountByProtocol(protocolId) {
    if (!protocolId) return 0;
    try {
      const q = query(
        this.deviationsCollection,
        where('protocolId', '==', protocolId),
        where('status', '==', 'open')
      );
      const snap = await getDocs(q);
      return snap.size;
    } catch (_) {
      return 0;
    }
  }

  /**
   * Create deviation from threshold breach (PRD-compliant, full statement).
   */
  async createFromThresholdBreach({
    organizationId,
    protocolId,
    protocolName,
    taskInstanceId,
    executionId,
    expectedComplianceValue,
    actualComplianceValue,
    riskId,
    riskName,
    thresholdId,
    thresholdValue,
    standardRequirementIds,
    incidentId,
    departmentId,
    roomId,
    executorId,
    executorName,
    biologicalEntities,
    failedSteps
  }) {
    const gapValue = (expectedComplianceValue ?? 100) - (actualComplianceValue ?? 0);
    const { requirementIds, requirementCodes, standardCodes } = await this.resolveStandardRequirementIdsForProtocol(protocolId);
    const reqIds = standardRequirementIds?.length ? standardRequirementIds : requirementIds;

    return this.createDeviationWithContext(
      {
        deviation_type: 'threshold_breach',
        deviation_category: 'compliance',
        severity: gapValue >= 40 ? 'critical' : gapValue >= 20 ? 'major' : 'minor',
        description: `Threshold breach: compliance ${actualComplianceValue ?? 0}% vs expected ${expectedComplianceValue ?? 100}%.`,
        detected_by: 'system',
        detection_method: 'threshold_evaluation',
        compliance_score_impact: gapValue,
        patient_safety_impact: gapValue >= 40 ? 'high' : 'medium'
      },
      {
        organizationId,
        taskInstanceId: taskInstanceId || executionId,
        taskExecutionId: executionId,
        protocolId,
        protocolName,
        departmentId,
        roomId,
        entityType: roomId ? 'room' : 'department',
        entityId: roomId || departmentId || '',
        locationPath: [departmentId, roomId].filter(Boolean).join(' / ') || '—',
        executorId,
        executorName: executorName || '',
        riskId,
        riskName,
        biologicalEntities: biologicalEntities || [],
        rpnAtTime: 0,
        thresholdId,
        thresholdType: 'absolute',
        thresholdValue,
        standardRequirementIds: reqIds,
        incidentIds: incidentId ? [incidentId] : [],
        failedSteps: failedSteps || [],
        expectedComplianceValue,
        actualComplianceValue,
        gapValue
      }
    );
  }

  async triggerCriticalDeviationAlert(deviation) {
    console.warn('⚠️ CRITICAL DEVIATION DETECTED:', deviation);
  }

  /**
   * Citire o deviație după ID (RCA UI / prefill).
   */
  async getDeviation(deviationId) {
    try {
      const snap = await getDoc(doc(this.deviationsCollection, deviationId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (e) {
      console.error('[DeviationService] getDeviation failed:', e);
      return null;
    }
  }

  /**
   * List deviations for an organization (newest first). Client-side filters optional.
   * @param {string} organizationId
   * @param {{ status?: string, severity?: string, protocolId?: string, isRecurring?: boolean }} filters - status/severity use 'all' to skip
   */
  async getDeviationsByOrganization(organizationId, filters = {}) {
    if (!organizationId) return [];
    try {
      const q = query(
        this.deviationsCollection,
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (filters.status && filters.status !== 'all') {
        items = items.filter((i) => i.status === filters.status);
      }
      if (filters.severity && filters.severity !== 'all') {
        items = items.filter((i) => i.severity === filters.severity);
      }
      if (filters.protocolId) {
        items = items.filter((i) => i.protocolId === filters.protocolId);
      }
      if (filters.isRecurring === true) {
        items = items.filter((i) => i.isRecurring === true);
      }

      return items;
    } catch (e) {
      console.error('[DeviationService] getDeviationsByOrganization failed:', e);
      return [];
    }
  }

  /**
   * Link RCA to deviation.
   */
  async linkRca(deviationId, rcaId, userId) {
    const { updateDoc, doc } = await import('firebase/firestore');
    await updateDoc(doc(this.deviationsCollection, deviationId), {
      rcaId,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  /**
   * Link CAPA to deviation.
   */
  async linkCorrectiveAction(deviationId, capaId, userId) {
    const { getDoc, doc, updateDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(this.deviationsCollection, deviationId));
    if (!snap.exists()) return;
    const correctiveActionIds = [...(snap.data().correctiveActionIds || []), capaId];
    await updateDoc(doc(this.deviationsCollection, deviationId), {
      correctiveActionIds,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }
}

export const deviationService = new DeviationService();
